import { PanelBody, Button, Notice } from '@wordpress/components';
import { __, sprintf } from '@wordpress/i18n';

import ColorPicker from '../../../../components/ColorPicker';
import BulkEdit from './bulk-edit';
import { MAX_SERIES } from '../../lib/chart-config';
import {
	addCategory,
	addSeries,
	patchSeries,
	removeCategory,
	removeSeries,
	setCell,
} from '../data-operations';

/**
 * The data grid.
 *
 * A spreadsheet a person can type into, plus a CSV box for the case the data
 * already exists somewhere else — which, for a chart, it usually does.
 */
export default function DataControls( { attributes, setAttributes, config } ) {
	const categories = attributes.categories || [];
	const series = attributes.series || [];
	const isRadial = config.isRadial;

	// A pie has no second series to plot, so the grid hides the extra columns
	// rather than showing controls that would not change the chart.
	const columns = isRadial ? series.slice( 0, 1 ) : series;

	return (
		<PanelBody title={ __( 'Data', 'brandy-blocks' ) }>
			{ /*
			 * The series live above the matrix rather than as its header row.
			 * A name typed into an 88px column header is unreadable, and the
			 * sidebar is too narrow to widen the columns enough to fix it —
			 * so the names get the full width here and the matrix header
			 * keeps only the colour chip that identifies each column.
			 */ }
			<div className="brandy-chart-grid__series-list">
				{ columns.map( ( one, index ) => (
					<SeriesRow
						key={ index }
						series={ one }
						index={ index }
						// The swatch shows the colour the series is actually
						// drawn in. Most series carry no override, and a blank
						// swatch would leave the author guessing which palette
						// slot they got.
						resolvedColor={
							config.series[ index ]
								? config.series[ index ].color
								: ''
						}
						canRemove={ ! isRadial && series.length > 1 }
						onChange={ ( patch ) =>
							setAttributes( {
								series: patchSeries( series, index, patch ),
							} )
						}
						onRemove={ () =>
							setAttributes( {
								series: removeSeries( series, index ),
							} )
						}
					/>
				) ) }
			</div>

			<div className="brandy-chart-grid">
				<div className="brandy-chart-grid__head">
					<span className="brandy-chart-grid__corner">
						{ isRadial
							? __( 'Slice', 'brandy-blocks' )
							: __( 'Category', 'brandy-blocks' ) }
					</span>
					{ columns.map( ( one, index ) => (
						<span
							key={ index }
							className="brandy-chart-grid__col-key"
							style={ { '--key-color': one.color } }
							title={ one.label }
						/>
					) ) }
				</div>

				{ categories.map( ( category, row ) => (
					<div key={ row } className="brandy-chart-grid__row">
						<input
							type="text"
							className="brandy-chart-grid__label"
							value={ category }
							aria-label={ sprintf(
								/* translators: %d: row number. */
								__( 'Category %d', 'brandy-blocks' ),
								row + 1
							) }
							onChange={ ( event ) =>
								setAttributes( {
									categories: categories.map( ( value, i ) =>
										i === row ? event.target.value : value
									),
								} )
							}
						/>
						{ columns.map( ( one, column ) => (
							<input
								key={ column }
								type="number"
								className="brandy-chart-grid__cell"
								value={ ( one.data || [] )[ row ] ?? 0 }
								aria-label={ sprintf(
									/* translators: 1: series name, 2: category name. */
									__( '%1$s, %2$s', 'brandy-blocks' ),
									one.label,
									category
								) }
								onChange={ ( event ) =>
									setAttributes( {
										series: setCell(
											series,
											column,
											row,
											event.target.value
										),
									} )
								}
							/>
						) ) }
						<Button
							className="brandy-chart-grid__remove"
							icon="no-alt"
							size="small"
							label={ __( 'Remove row', 'brandy-blocks' ) }
							disabled={ categories.length <= 1 }
							onClick={ () =>
								setAttributes(
									removeCategory( categories, series, row )
								)
							}
						/>
					</div>
				) ) }
			</div>

			<div className="brandy-chart-grid__actions">
				<Button
					variant="secondary"
					size="compact"
					onClick={ () =>
						setAttributes( addCategory( categories, series ) )
					}
				>
					{ __( 'Add row', 'brandy-blocks' ) }
				</Button>
				{ ! isRadial && (
					<Button
						variant="secondary"
						size="compact"
						disabled={ series.length >= MAX_SERIES }
						onClick={ () =>
							setAttributes( {
								series: addSeries( series, categories.length ),
							} )
						}
					>
						{ __( 'Add series', 'brandy-blocks' ) }
					</Button>
				) }
			</div>

			{ ! isRadial && series.length >= MAX_SERIES && (
				<Notice status="info" isDismissible={ false }>
					{ __(
						'Eight series is the ceiling. A ninth colour would not stay distinguishable to colourblind readers — fold the smallest into an “Other” row, or split this into two charts.',
						'brandy-blocks'
					) }
				</Notice>
			) }

			{ isRadial && series.length > 1 && (
				<Notice status="warning" isDismissible={ false }>
					{ __(
						'A pie or donut plots one series. Only the first is drawn; switch to a bar chart to show the rest.',
						'brandy-blocks'
					) }
				</Notice>
			) }

			<BulkEdit
				attributes={ attributes }
				setAttributes={ setAttributes }
			/>
		</PanelBody>
	);
}

function SeriesRow( {
	series,
	index,
	canRemove,
	resolvedColor,
	onChange,
	onRemove,
} ) {
	return (
		<div className="brandy-chart-grid__series">
			<ColorPicker
				color={ series.color || resolvedColor }
				onChange={ ( color ) => onChange( { color: color || '' } ) }
			/>
			<input
				type="text"
				className="brandy-chart-grid__series-name"
				value={ series.label }
				aria-label={ sprintf(
					/* translators: %d: series number. */
					__( 'Series %d name', 'brandy-blocks' ),
					index + 1
				) }
				onChange={ ( event ) =>
					onChange( { label: event.target.value } )
				}
			/>
			{ canRemove && (
				<Button
					icon="no-alt"
					size="small"
					label={ __( 'Remove series', 'brandy-blocks' ) }
					onClick={ onRemove }
				/>
			) }
		</div>
	);
}
